import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import React, { useEffect, useMemo, useState } from "react";
import { colFlexStyle, commonContainerWrapper } from "../../Common/styles/flex";
import { useParams, useSearchParams } from "react-router-dom";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import GlobalTagsCard from "../GlobalTagsCard";
import GlobalNotesList from "../GlobalNotesList";
import GlobalParticipantNotesCard from "../GlobalParticipantNotesCard";
import EditParticipantDrawer from "./EditParticipantDrawer";
import RedactionPreviewModal from "../Modals/RedactionPreviewModal";
import RedactionHistoryModal from "../Modals/RedactionHistoryModal";
import EditIcon from "@mui/icons-material/Edit";
import { DateTime } from "luxon";
import { genderOptions } from "../../../const/participant";

const REDACTION_STATUS_CHIP: Record<string, { label: string; color: "warning" | "error" | "default" }> = {
  deactivated: { label: "Deactivated", color: "warning" },
  redacted: { label: "Redacted", color: "error" },
  deleted: { label: "Deleted", color: "error" },
};

interface Props {
  /** Raw participant from the parent fetch (GlobalParticipantTabs). */
  participant?: any;
  /** True when redactionStatus is 'deactivated' or 'redacted'. */
  isLocked?: boolean;
  /** Triggers a re-fetch in the parent after a state-changing action. */
  refreshParticipant?: () => void;
}

const ParticipantDetails: React.FC<Props> = ({
  participant,
  isLocked: lockedProp,
  refreshParticipant,
}) => {
  const { participantId } = useParams();
  const [, setSearchParams] = useSearchParams();
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [showRegistryRedactModal, setShowRegistryRedactModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Deactivation
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  // Reactivation (deactivated → active, no PII restoration needed)
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [reactivateReason, setReactivateReason] = useState("");
  const [reactivating, setReactivating] = useState(false);

  // Revival
  const [showReviveDialog, setShowReviveDialog] = useState(false);
  const [reviveJustification, setReviveJustification] = useState("");
  const [reviving, setReviving] = useState(false);

  // Redaction-history popup, opened by clicking the status chip.
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Mirrors the BE deactivate enrolment guard (deletion.service.ts) — the
  // button is hidden behind the same precondition the API will enforce so the
  // admin gets fail-fast feedback instead of a 409 after submitting.
  const [unresolvedEnrolmentCount, setUnresolvedEnrolmentCount] = useState<
    number | null
  >(null);

  const loading = !participant;

  // Format the parent-provided participant for display: DOB → MM/DD/YYYY,
  // lastStudyDate → MM/DD/YYYY, gender → displayGender label.
  const data = useMemo(() => {
    if (!participant) return null;
    const next: any = { ...participant };
    if (next.dob) {
      const dobDate = DateTime.fromFormat(next.dob, "yyyy-MM-dd");
      if (dobDate.isValid) next.dob = dobDate.toFormat("MM/dd/yyyy");
    }
    if (next.lastStudyDate) {
      const lsd = DateTime.fromFormat(
        String(next.lastStudyDate).split("T")[0],
        "yyyy-MM-dd",
      );
      if (lsd.isValid) next.lastStudyDate = lsd.toFormat("MM/dd/yyyy");
    }
    if (next.gender) {
      next.displayGender =
        genderOptions.find((opt: any) => opt.value === next.gender)?.label ||
        "";
    }
    return next;
  }, [participant]);

  const tags = useMemo(
    () =>
      (participant?.tags ?? []).map((tag: any) => ({
        id: tag?.tag?.id,
        name: tag?.tag?.name,
        studyId: tag?.studyId,
        category: tag?.tag?.category,
        slug: tag?.tag?.slug,
        isEditable: tag?.tag?.isEditable,
      })),
    [participant],
  );

  useEffect(() => {
    if (!participantId || participant?.redactionStatus !== "active") {
      setUnresolvedEnrolmentCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await http.get(
          `/admin/registry/participants/${participantId}/studies`,
        );
        const rows: any[] = res.data?.data ?? [];
        const unresolved = rows.filter(
          (r) => !r?.redactedAt && !r?.consentWithdrawnAt,
        ).length;
        if (!cancelled) setUnresolvedEnrolmentCount(unresolved);
      } catch {
        if (!cancelled) setUnresolvedEnrolmentCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [participantId, participant?.redactionStatus]);

  // Mirror current participant name to the URL query param so the header
  // breadcrumb stays accurate after revive (re-decrypted PII).
  useEffect(() => {
    if (!participant) return;
    const name =
      `${participant.firstName || ""} ${participant.lastName || ""}`.trim() ||
      participant.email;
    setSearchParams({ participant: name ?? "" }, { replace: true });
  }, [participant, setSearchParams]);

  const refreshDetails = () => {
    refreshParticipant?.();
  };

  const closeEditDrawer = () => {
    setShowEditDrawer(false);
  };

  const handleDeactivate = async () => {
    try {
      setDeactivating(true);
      await http.post(`/admin/participants/${participantId}/deactivate`, {
        reason: deactivateReason.trim(),
      });
      setShowDeactivateConfirm(false);
      setDeactivateReason("");
      toastMessage("success", "Participant deactivated");
      refreshDetails();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setReactivating(true);
      await http.post(`/admin/participants/${participantId}/reactivate`, {
        reason: reactivateReason.trim(),
      });
      setShowReactivateDialog(false);
      setReactivateReason("");
      toastMessage("success", "Participant re-activated");
      refreshDetails();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setReactivating(false);
    }
  };

  const handleRevive = async () => {
    try {
      setReviving(true);
      await http.post(`/admin/participants/${participantId}/revive`, {
        reason: reviveJustification.trim(),
      });
      setShowReviveDialog(false);
      setReviveJustification("");
      toastMessage("success", "Participant revived");
      refreshDetails();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setReviving(false);
    }
  };

  const redactionStatus: string = data?.redactionStatus || "active";
  const isActive = redactionStatus === "active";
  const isDeactivated = redactionStatus === "deactivated";
  const isRedacted = redactionStatus === "redacted";
  // Trust the parent-provided isLocked when present so the lock semantics
  // stay identical across all tabs; fall back to the local computation if
  // ParticipantDetails is ever rendered standalone (defensive default).
  const isLocked = lockedProp ?? (isDeactivated || isRedacted);
  const statusChip = REDACTION_STATUS_CHIP[redactionStatus];

  return (
    <Box>
      <Box sx={commonContainerWrapper}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h2" color="text.primary">
              Participant Details
            </Typography>
            {statusChip && (
              <Tooltip arrow title="Click for reason, actor, and full history">
                <Chip
                  label={statusChip.label}
                  color={statusChip.color}
                  size="small"
                  onClick={() => setShowHistoryModal(true)}
                  sx={{ fontWeight: 600, cursor: "pointer" }}
                />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setShowEditDrawer(true)}
              disabled={loading || isLocked}
            >
              Edit
            </Button>
            {isActive && (
              <Tooltip
                arrow
                title={
                  unresolvedEnrolmentCount && unresolvedEnrolmentCount > 0
                    ? `Cannot deactivate: ${unresolvedEnrolmentCount} study enrolment(s) are not yet redacted or consent-withdrawn. Resolve them in the Studies tab first.`
                    : ""
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setShowDeactivateConfirm(true)}
                    disabled={
                      loading ||
                      (unresolvedEnrolmentCount !== null &&
                        unresolvedEnrolmentCount > 0)
                    }
                  >
                    Deactivate
                  </Button>
                </span>
              </Tooltip>
            )}
            {isDeactivated && (
              <Button
                variant="outlined"
                color="success"
                onClick={() => setShowReactivateDialog(true)}
                disabled={loading}
                title="Reverses the deactivation while no PII has been redacted yet."
              >
                Reactivate
              </Button>
            )}
            {isDeactivated && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setShowRegistryRedactModal(true)}
                disabled={loading}
                title="Database Redact requires every study enrolment to be redacted or have consent withdrawn first. See the Studies tab."
              >
                Database Redact
              </Button>
            )}
            {isRedacted && (
              <Button
                variant="outlined"
                color="success"
                onClick={() => setShowReviveDialog(true)}
                disabled={loading}
              >
                Revive
              </Button>
            )}
          </Box>
        </Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              my: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              my: 3,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  System ID
                </Typography>
                {data?.participantCode ? (
                  <Tooltip
                    title={copiedCode ? "Copied!" : "Click to copy"}
                    arrow
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                        userSelect: "none",
                        width: "fit-content",
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(data.participantCode);
                        setCopiedCode(true);
                        setTimeout(() => setCopiedCode(false), 1500);
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontFamily="monospace"
                        color="text.primary"
                      >
                        {data.participantCode}
                      </Typography>
                      <ContentCopyIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    </Box>
                  </Tooltip>
                ) : (
                  <Typography variant="subtitle1" color="text.primary">
                    -
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  First Name
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.firstName || "-"}
                </Typography>
              </Box>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Last Name
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.lastName || "-"}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Email
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.email || "-"}
                </Typography>
              </Box>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Phone Number
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.phoneNumber || "-"}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Gender
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.displayGender || "-"}
                </Typography>
              </Box>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  DOB
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.dob || "-"}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Occupation
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.occupation || "-"}
                </Typography>
              </Box>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Language
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.lang || "-"}
                </Typography>
              </Box>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Last Study Date
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.lastStudyDate || "-"}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Address Line 1
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.address?.line1 || "-"}
                </Typography>
              </Box>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Address Line 2
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.address?.line2 || "-"}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  City
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.address?.city || "-"}
                </Typography>
              </Box>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  State
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.address?.state || "-"}
                </Typography>
              </Box>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Country
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.address?.country || "-"}
                </Typography>
              </Box>
              <Box sx={{ ...colFlexStyle, minWidth: "240px", gap: "10px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={"regular"}
                  color="text.primary"
                >
                  Zip Code
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {data?.address?.zipcode || "-"}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <GlobalTagsCard
        tagsList={tags}
        refreshPage={refreshDetails}
        canEdit={!isLocked}
      />
      <GlobalParticipantNotesCard canEdit={!isLocked} />
      <GlobalNotesList canEdit={!isLocked} />

      {showEditDrawer && (
        <EditParticipantDrawer
          showModal={showEditDrawer}
          closeModal={closeEditDrawer}
          refreshPage={refreshDetails}
          participantData={data}
        />
      )}

      {showHistoryModal && participantId && (
        <RedactionHistoryModal
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          participantId={participantId}
          currentStatus={
            (redactionStatus as
              | "active"
              | "deactivated"
              | "redacted"
              | "deleted") ?? "active"
          }
        />
      )}

      {showRegistryRedactModal && (
        <RedactionPreviewModal
          open={showRegistryRedactModal}
          onClose={() => setShowRegistryRedactModal(false)}
          fetchPreview={() =>
            http.get(`/admin/participants/${participantId}/redact/preview`)
              .then((r: any) => r.data.data)
          }
          onConfirm={async (reason: string) => {
            try {
              await http.post(`/admin/participants/${participantId}/redact`, {
                reason,
              });
              setShowRegistryRedactModal(false);
              toastMessage("success", "Participant redacted");
              refreshDetails();
            } catch (err: any) {
              if (err?.response?.status === 409) {
                errorToastMessage(
                  new Error(
                    "Registry redact blocked: every study enrolment must be redacted or have consent withdrawn first. Open the Studies tab.",
                  ),
                );
              } else {
                errorToastMessage(err as Error);
              }
            }
          }}
        />
      )}

      {/* Deactivation confirm dialog */}
      <Dialog
        open={showDeactivateConfirm}
        onClose={() => !deactivating && setShowDeactivateConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Deactivate Participant</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Deactivating this participant will prevent new data entry on their
            record. This is reversible — the participant can be re-activated or
            proceeded to registry redaction.
          </DialogContentText>
          <TextField
            label="Reason for deactivation"
            required
            multiline
            rows={3}
            fullWidth
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
            disabled={deactivating}
            error={deactivateReason.trim().length === 0}
            helperText={
              deactivateReason.trim().length === 0
                ? "Required — recorded in the audit trail."
                : `${deactivateReason.length}/1000`
            }
            inputProps={{ maxLength: 1000 }}
            placeholder="e.g. Participant requested temporary pause; will revisit in 30 days."
          />
          {deactivating && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Deactivating participant…
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeactivateConfirm(false);
              setDeactivateReason("");
            }}
            disabled={deactivating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleDeactivate}
            disabled={deactivating || deactivateReason.trim().length === 0}
            startIcon={
              deactivating ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {deactivating ? "Deactivating…" : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate dialog (deactivated → active) */}
      <Dialog
        open={showReactivateDialog}
        onClose={() => !reactivating && setShowReactivateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reactivate Participant</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will reverse the deactivation and set the participant's
            status back to active. No PII has been redacted yet, so no
            escrow restoration is needed.
          </DialogContentText>
          <TextField
            label="Reason for re-activation"
            required
            multiline
            rows={3}
            fullWidth
            value={reactivateReason}
            onChange={(e) => setReactivateReason(e.target.value)}
            disabled={reactivating}
            error={reactivateReason.trim().length === 0}
            helperText={
              reactivateReason.trim().length === 0
                ? "Required — recorded in the audit trail."
                : `${reactivateReason.length}/1000`
            }
            inputProps={{ maxLength: 1000 }}
            placeholder="e.g. Deactivation initiated in error; participant remains enrolled."
          />
          {reactivating && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Re-activating participant…
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowReactivateDialog(false);
              setReactivateReason("");
            }}
            disabled={reactivating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleReactivate}
            disabled={reactivating || reactivateReason.trim().length === 0}
            startIcon={
              reactivating ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {reactivating ? "Re-activating…" : "Reactivate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revival dialog */}
      <Dialog
        open={showReviveDialog}
        onClose={() => !reviving && setShowReviveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revive Participant</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will restore the participant's PII from escrow and set their
            status back to active. A written reason is recorded in the audit
            trail; if the grace period has elapsed, the same reason is treated
            as the override justification.
          </DialogContentText>
          <TextField
            label="Reason for revival"
            required
            multiline
            rows={3}
            fullWidth
            value={reviveJustification}
            onChange={(e) => setReviveJustification(e.target.value)}
            disabled={reviving}
            error={reviveJustification.trim().length === 0}
            helperText={
              reviveJustification.trim().length === 0
                ? "Required — recorded in the audit trail."
                : `${reviveJustification.length}/1000`
            }
            inputProps={{ maxLength: 1000 }}
            placeholder="e.g. Re-enrolment requested with fresh consent on 2026-04-25."
          />
          {reviving && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Reviving participant — restoring PII from escrow.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowReviveDialog(false);
              setReviveJustification("");
            }}
            disabled={reviving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleRevive}
            disabled={reviving || reviveJustification.trim().length === 0}
            startIcon={
              reviving ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {reviving ? "Reviving…" : "Revive"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParticipantDetails;
